import { useEffect, useId, useState } from 'react'

/**
 * Full-screen entry animation when opening a card from a notification (owner app).
 * English UI per product rules.
 */
export default function CardEntryTransition({ visible, cardName, imageUrl }) {
  const ringGradId = useId().replace(/:/g, '')
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (!visible) {
      setProgress(0)
      return undefined
    }
    const started = Date.now()
    const durationMs = 2200
    let frame
    const tick = () => {
      const t = Math.min(1, (Date.now() - started) / durationMs)
      setProgress(Math.round(t * 100))
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [visible])

  if (!visible) return null

  const safeImg =
    imageUrl && !String(imageUrl).startsWith('gs://') ? imageUrl : '/placeholder-card.png'

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center px-6"
      style={{
        background: 'radial-gradient(ellipse at center, rgba(10, 14, 39, 0.97) 0%, #030712 100%)',
      }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-60" aria-hidden>
        {Array.from({ length: 48 }).map((_, i) => (
          <span
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${1 + (i % 3)}px`,
              height: `${1 + (i % 3)}px`,
              left: `${(i * 17.3) % 100}%`,
              top: `${(i * 23.7 + (i % 5) * 11) % 100}%`,
              background:
                i % 4 === 0
                  ? 'rgba(180, 156, 245, 0.55)'
                  : i % 4 === 1
                    ? 'rgba(180, 156, 245, 0.45)'
                    : i % 4 === 2
                      ? 'rgba(52, 211, 153, 0.4)'
                      : 'rgba(255, 255, 255, 0.35)',
              boxShadow: '0 0 6px currentColor',
              opacity: 0.35 + (i % 7) * 0.08,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 flex w-full max-w-sm flex-col items-center">
        <img
          src="/m1nt-logo.png"
          alt="M1NT"
          className="mb-6 h-8 w-auto object-contain opacity-95"
          style={{ filter: 'drop-shadow(0 0 12px rgba(180, 156, 245, 0.35))' }}
        />

        <div
          className="relative mb-8 w-[min(240px,70vw)] overflow-hidden rounded-2xl"
          style={{
            aspectRatio: '3/4',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 0 40px rgba(180, 156, 245, 0.15), 0 25px 50px rgba(0,0,0,0.5)',
          }}
        >
          <img
            src={safeImg}
            alt=""
            className="h-full w-full object-cover"
            onError={(e) => {
              e.target.src = '/placeholder-card.png'
              e.target.onerror = null
            }}
          />
        </div>

        <div className="relative mb-5 h-28 w-28">
          <svg className="-rotate-90" viewBox="0 0 100 100" aria-hidden>
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="6"
            />
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              stroke={`url(#${ringGradId})`}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${(progress / 100) * 264} 264`}
            />
            <defs>
              <linearGradient id={ringGradId} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#22d3ee" />
                <stop offset="100%" stopColor="#a855f7" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-black tabular-nums text-white">{progress}%</span>
          </div>
        </div>

        <h2 className="mb-1 text-center text-lg font-black uppercase tracking-wide text-cyan-400">
          Verifying card
        </h2>
        <p className="mb-2 text-center text-sm text-gray-400">Checking records…</p>
        {cardName ? (
          <p className="max-w-full truncate text-center text-xs font-medium text-white/70">
            {cardName}
          </p>
        ) : null}
      </div>
    </div>
  )
}
